import Link from 'next/link';
import CategoryLabel from '../category/CategoryLabel';
import DateLabel from '../date/DateLabel';
import WysiwygBlock from '../blocks/WysiwygBlock';
import TitleH3 from '../common/TitleH3';
import { CardProps } from './types';
import FFBBDirigeant from '../../../../public/webp/FFBBDirigeant.webp';
import FFBBJoueur from '../../../../public/webp/FFBBJoueur.webp';
import FFBBOfficiel from '../../../../public/webp/FFBBOfficiel.webp';
import FFBBTechnicien from '../../../../public/webp/FFBBTechnicien.webp';
import FFBBTransverse from '../../../../public/webp/FFBBTransverse.webp';
import Image from 'next/image';

export interface FormationCardProps extends CardProps {
  domain?: string;
  mode?: string;
  typeCard?: string;
  duration_hours?: string;
  date_start?: string;
  date_end?: string;
  postal_code?: string;
  location?: string;
  reference?: string;
  subscribe_button?: string;
  entity?: string;
}

export default function CardFormation(props: FormationCardProps) {
  const {
    callToActionUrl,
    category,
    description,
    displayDescription = true,
    duration,
    domain,
    image,
    imageAlt,
    imageBlur,
    imageHeight,
    imageSizes,
    isoDate,
    dateLabelType = 'default',
    grayBackground,
    mode,
    photoCount = 0,
    title,
    titleUppercase = true,
    titleBold = false,
    titleBlockStyles = '',
    type,
    typeCard = 'formation',
    duration_hours,
    date_start,
    date_end,
    postal_code,
    location,
    reference,
    entity,
    subscribe_button,
  } = props;

  const modeString =
    mode === 'in_person'
      ? 'Présentielle'
      : mode === 'remote'
        ? 'Distancielle'
        : mode === 'mixed'
          ? 'Mixte'
          : '';
  const link =
    callToActionUrl && callToActionUrl.external
      ? callToActionUrl.url
      : callToActionUrl
        ? `${callToActionUrl.url}/${callToActionUrl.slug}`
        : '';
  const target = callToActionUrl && callToActionUrl.external ? '_blank' : '_self';
  let height = 'pb-[160px]';
  let imageSizesValue = imageSizes ?? '(min-width: 66em) 30vw, (min-width: 44em) 40vw, 100vw';
  let titleSize: 'default' | 'small' | 'medium' | 'big' = image ? 'default' : 'big';
  if (titleBlockStyles) {
    titleSize = titleBlockStyles as 'default' | 'small' | 'medium' | 'big';
    if (!image) {
      titleSize = 'big';
    }
  }

  const imageDomain = domain
    ? domain === 'Dirigeant'
      ? FFBBDirigeant
      : domain === 'Joueur'
        ? FFBBJoueur
        : domain === 'Officiel'
          ? FFBBOfficiel
          : domain === 'Technicien'
            ? FFBBTechnicien
            : domain === 'Transversales'
              ? FFBBTransverse
              : FFBBTransverse
    : FFBBTransverse;

  let imageSrc = image ? (!image.includes('htm') ? image : imageDomain) : imageDomain;

  const imageAltFinal = 'Illustration ' + domain;

  const cardContent = (
    <div className={`card card-compact min-h-[200px] w-full shadow-none`}>
      {imageSrc && (
        <figure className={`relative ${height} ${imageSizesValue} w-full overflow-hidden`}>
          <Image
            alt={imageAltFinal}
            src={imageSrc}
            quality={100}
            fill
            sizes=""
            className=""
            style={{
              objectFit: 'contain',
            }}
          />
          {duration && (
            <div className="absolute bottom-[20px] right-[20px] flex max-w-max items-center gap-2.5">
              <span className="rounded bg-white p-[5px] text-xs font-semibold uppercase text-gray-400 backdrop-blur-sm">
                {duration}
              </span>
            </div>
          )}
        </figure>
      )}
      <div className="card-body gap-4" style={{ padding: '0 0 10px 0' }}>
        {typeCard === 'formation' && reference && (
          <CategoryLabel category={`réf. ${reference}`} styles={`${!image ? 'mt-[5px]' : ''}`} />
        )}
        {typeCard === 'session' && (
          <div className={`mt-[5px] flex max-w-max rounded gradient-bg`}>
            <span className="cursor-pointer truncate text-nowrap px-2 py-1 text-xs font-bold uppercase leading-[10px] gradient-category-text hover:overflow-visible hover:whitespace-normal">
              {date_start && (
                <DateLabel
                  isoDate={date_start || ''}
                  type={date_start.endsWith('00:00:00') ? 'd MMMM yyyy' : 'd MMMM yyyy HH:mm'}
                  color="none"
                />
              )}
              {date_start && date_end && date_start !== date_end && ' au '}
              {date_end && date_start !== date_end && (
                <DateLabel
                  isoDate={date_end || ''}
                  type={date_end.endsWith('00:00:00') ? 'd MMMM yyyy' : 'd MMMM yyyy HH:mm'}
                  color="none"
                />
              )}
            </span>
          </div>
        )}
        {title && (
          <TitleH3
            size={'medium'}
            color="black"
            titleUpperCase={false}
            titleBold={true}
            titleTracking=""
            heading={true}
            customFont={false}
            styles=""
          >
            {title}
          </TitleH3>
        )}

        {typeCard === 'formation' && category && (
          <p className="cursor-pointer truncate text-sm text-gray-500 hover:overflow-visible hover:whitespace-normal">
            {category}
          </p>
        )}

        {typeCard === 'session' && reference && (
          <p className="cursor-pointer truncate text-sm text-gray-500 hover:overflow-visible hover:whitespace-normal">
            {`${entity ? `${entity.toUpperCase()} | ` : ''}`}
            {`réf. ${reference}`}
          </p>
        )}
        <div className="flex flex-row items-center gap-2">
          {typeCard === 'formation' && modeString && (
            <CategoryLabel
              category={modeString}
              styles={`${!image ? 'mb-[5px]' : ''}`}
              type="neutral"
            />
          )}
          {typeCard === 'formation' && duration_hours && (
            <CategoryLabel
              category={duration_hours}
              styles={`${!image ? 'mb-[5px]' : ''}`}
              type="neutral"
            />
          )}

          {typeCard === 'session' && postal_code && (
            <CategoryLabel
              category={postal_code}
              styles={`${!image ? 'mb-[5px]' : ''}`}
              type="neutral"
            />
          )}

          {typeCard === 'session' && location && (
            <div className="relative max-w-[130px] group">
              <CategoryLabel
                category={
                  typeof location === 'string' && location.length > 20
                    ? `${location.slice(0, 20)}…`
                    : location
                }
                styles={`${!image ? 'mb-[5px]' : ''}`}
                type="neutral"
              />
              {typeof location === 'string' && location.length > 20 && (
                <div className="absolute left-0 top-full z-10 mt-1 hidden w-max max-w-xs rounded bg-white px-2 py-1 text-xs text-gray-700 shadow-lg group-hover:block">
                  {location}
                </div>
              )}
            </div>
          )}

          {subscribe_button && (
            <a
              href={subscribe_button}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto rounded-full border border-gray-200 px-2 py-2 text-xs font-bold text-gray-700 transition-all hover:bg-gray-200"
              style={{ borderWidth: '1px' }}
            >
              Je m&apos;inscris
            </a>
          )}
        </div>
        {displayDescription && description && (
          <div className="line-clamp-4 grow">
            <WysiwygBlock html={description || ''} />
          </div>
        )}
        {isoDate && <DateLabel isoDate={isoDate} type={dateLabelType} styles="text-xs" />}
      </div>
    </div>
  );

  if (typeCard === 'formation' && callToActionUrl) {
    return (
      <Link href={link} target={target}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
